import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUp, Star } from "lucide-react";
import { MaskedLines } from "@/components/Reveal";
import ZipCTA from "@/components/denim/ZipCTA";
import RivetCorners from "@/components/denim/RivetCorners";

const EASE = [0.16, 1, 0.3, 1];
const DENIM = "url('/img/denim/denim-texture.jpg')";

export default function Hero() {
    const { scrollY } = useScroll();
    const yMain = useTransform(scrollY, [0, 700], [0, 50]);
    const yTile1 = useTransform(scrollY, [0, 700], [0, 110]);

    return (
        <section
            className="denim-bg relative flex min-h-screen items-center overflow-hidden pb-24 pt-28 md:pt-32"
            style={{ backgroundImage: DENIM }}
            data-testid="hero-denim-bag"
        >
            <div className="absolute inset-0 bg-obsidian/60" aria-hidden="true" />
            <div className="pointer-events-none absolute -right-40 -top-40 h-[560px] w-[560px] rounded-full bg-indigodeep/60 blur-[140px]" aria-hidden="true" />
            <div className="pointer-events-none absolute -left-52 bottom-0 h-[480px] w-[480px] rounded-full bg-gold/10 blur-[140px]" aria-hidden="true" />

            {/* bag panel seams */}
            <svg
                aria-hidden="true"
                viewBox="0 0 1440 900"
                preserveAspectRatio="none"
                className="pointer-events-none absolute inset-0 h-full w-full opacity-70"
            >
                <path d="M250,-20 C 290,300 260,620 230,940" fill="none" stroke="rgba(212,175,55,0.22)" strokeWidth="2" strokeDasharray="10 8" />
                <path d="M1190,-20 C 1150,280 1180,640 1210,940" fill="none" stroke="rgba(212,175,55,0.22)" strokeWidth="2" strokeDasharray="10 8" />
                <path d="M-20,470 C 400,430 1040,510 1460,460" fill="none" stroke="rgba(212,175,55,0.16)" strokeWidth="2" strokeDasharray="12 9" />
            </svg>

            {/* outer bag stitching */}
            <div className="pointer-events-none absolute inset-3 z-10 rounded-2xl border-2 border-dashed border-gold/20 md:inset-4" aria-hidden="true" />

            <div className="relative z-20 mx-auto w-full max-w-[1400px] px-5 md:px-8 lg:px-10">
                <div className="grid items-center gap-12 pt-10 md:pt-14 lg:grid-cols-12 lg:gap-10">
                    <div className="lg:col-span-6">
                        <h1 className="font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight sm:text-6xl lg:text-6xl">
                            <MaskedLines delay={0.3} lines={["Denim,", <span key="l">Perfected<span className="text-gold">.</span></span>]} />
                        </h1>

                        {/* back pocket holding the tagline */}
                        <motion.div
                            initial={{ opacity: 0, y: 28 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.55, duration: 0.8, ease: EASE }}
                            data-testid="hero-tag-pocket"
                            className="tag-pocket relative mt-8 w-fit max-w-full rounded-b-[40px] border border-gold/25 bg-black/30 px-6 pb-7 pt-5 md:px-8"
                            style={{ perspective: "800px" }}
                        >
                            <div className="pointer-events-none absolute inset-x-2 top-2 bottom-2 rounded-b-[32px] border-2 border-dashed border-gold/40" aria-hidden="true" />
                            <span className="rivet absolute left-2.5 top-8 h-3 w-3 rounded-full" aria-hidden="true" />
                            <span className="rivet absolute right-2.5 top-8 h-3 w-3 rounded-full" aria-hidden="true" />
                            <MaskedLines
                                delay={0.6}
                                inView={false}
                                lines={["The Art of Denim.", "The Science of Fit."]}
                                lineClassName="font-display text-lg font-semibold leading-snug text-ink/95 md:text-2xl"
                            />
                            {/* denim flap — lifts away on hover to read the pocket */}
                            <div
                                aria-hidden="true"
                                className="pocket-flap pointer-events-none absolute inset-x-0 top-0 hidden h-full rounded-b-[40px] border border-gold/25 bg-[#0e1116] origin-top md:block"
                                style={{ backgroundImage: "url('/img/denim/denim-texture.jpg')", backgroundSize: "cover", backgroundPosition: "center" }}
                            >
                                <div className="absolute inset-0 bg-obsidian/40" />
                                <div className="absolute inset-x-2 top-2 bottom-2 rounded-b-[32px] border-2 border-dashed border-gold/40" />
                                <span className="rivet absolute left-2.5 top-8 h-3 w-3 rounded-full" />
                                <span className="rivet absolute right-2.5 top-8 h-3 w-3 rounded-full" />
                                <span className="absolute inset-0 grid place-items-center">
                                    <span className="mt-8 flex flex-col items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-gold">
                                        <ArrowUp size={14} /> Lift to read
                                    </span>
                                </span>
                            </div>
                        </motion.div>

                        <motion.p
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.8, duration: 0.8, ease: EASE }}
                            className="mt-7 max-w-md text-sm leading-relaxed text-mute md:text-base"
                        >
                            URWAN is a denim house from Bangalore — limited-edition performance denim,
                            cut in small batches and covered for life against stitch and tear.
                        </motion.p>
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.95, duration: 0.8, ease: EASE }}
                            className="mt-9 flex flex-wrap gap-4"
                        >
                            <ZipCTA to="/shop" testid="hero-cta-shop">
                                Explore the Collection
                            </ZipCTA>
                            <ZipCTA to="/about" variant="ghost" testid="hero-cta-about">
                                Our Philosophy
                            </ZipCTA>
                        </motion.div>

                        {/* leather patch holding the stats */}
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.15, duration: 0.9, ease: EASE }}
                            data-testid="hero-leather-patch"
                            className="leather-patch mt-11 w-fit rounded-sm border border-black/50 px-7 py-4 md:px-9"
                        >
                            <div className="flex flex-wrap gap-8 md:gap-12">
                                {[
                                    ["20+", "Limited Styles"],
                                    ["100%", "Stitch & Tear Cover"],
                                    ["Est.", "Bangalore, India"],
                                ].map(([v, l]) => (
                                    <div key={l}>
                                        <p className="font-display text-2xl font-bold text-[#ecd29b]">{v}</p>
                                        <p className="mt-1 text-[10px] font-mono uppercase tracking-[0.2em] text-[#c7a371]">
                                            {l}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </div>

                    {/* front pocket holding the campaign shot */}
                    <div className="relative h-[440px] sm:h-[540px] lg:col-span-6 lg:h-[600px]">
                        <motion.div
                            style={{ y: yMain }}
                            initial={{ clipPath: "inset(0 0 100% 0)", opacity: 0.4 }}
                            animate={{ clipPath: "inset(0 0 0% 0)", opacity: 1 }}
                            transition={{ delay: 0.35, duration: 1.2, ease: EASE }}
                            className="absolute inset-y-0 right-0 w-full md:w-[74%]"
                            data-testid="hero-collage"
                        >
                            <div className="relative h-full overflow-hidden border border-gold/25 bg-black/30 shadow-2xl shadow-black/60">
                                <img
                                    src="/img/urwan/2J7A0613-scaled.jpg"
                                    alt="URWAN campaign — model in denim shirt and jeans"
                                    className="h-full w-full object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-obsidian/60 via-transparent to-transparent" />
                                <div className="pointer-events-none absolute inset-3 border-2 border-dashed border-gold/35" aria-hidden="true" />
                                <RivetCorners inset="10px" />
                            </div>
                        </motion.div>
                        {/* coin pocket tile */}
                        <motion.div
                            style={{ y: yTile1 }}
                            initial={{ opacity: 0, y: 70 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.8, duration: 1, ease: EASE }}
                            className="absolute bottom-10 left-0 z-10 hidden w-44 -rotate-[4deg] md:block lg:w-52"
                        >
                            <div className="relative overflow-hidden rounded-b-[28px] border border-gold/25 bg-black/40 shadow-2xl shadow-black/60">
                                <img src="/img/urwan/2J7A0775.jpg" alt="URWAN selvedge raw denim" className="h-full w-full object-cover" />
                                <div className="pointer-events-none absolute inset-1.5 rounded-b-[22px] border border-dashed border-gold/40" aria-hidden="true" />
                            </div>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.7 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 1.2, duration: 0.9, ease: EASE }}
                            className="absolute -left-2 top-6 z-20 hidden h-28 w-28 place-items-center rounded-full border border-gold/30 bg-obsidian/75 backdrop-blur sm:grid lg:-left-6"
                            data-testid="hero-stamp"
                        >
                            <svg viewBox="0 0 120 120" className="animate-spin-slower absolute inset-0 h-full w-full">
                                <defs>
                                    <path id="stampCircle" d="M60,60 m-44,0 a44,44 0 1,1 88,0 a44,44 0 1,1 -88,0" />
                                </defs>
                                <text className="fill-mute" style={{ fontSize: "10px", letterSpacing: "2.5px", fontFamily: "'JetBrains Mono', monospace" }}>
                                    <textPath href="#stampCircle">URWAN · LUXURY DENIM · MADE IN INDIA ·</textPath>
                                </text>
                            </svg>
                            <Star className="h-6 w-6 text-gold" fill="currentColor" />
                        </motion.div>
                    </div>
                </div>
            </div>

            {/* hanging zipper pull scroll cue */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.5, duration: 1 }}
                className="absolute bottom-0 left-1/2 z-20 hidden -translate-x-1/2 flex-col items-center lg:flex"
                data-testid="hero-scroll-pull"
            >
                <motion.div
                    animate={{ y: [0, 7, 0] }}
                    transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                    className="flex flex-col items-center"
                >
                    <span className="h-8 w-[3px] bg-gradient-to-b from-gold/10 to-gold/80" aria-hidden="true" />
                    <span className="grid h-9 w-5 place-items-center rounded-md border-2 border-gold bg-obsidian/85 shadow-[0_0_14px_rgba(212,175,55,0.35)]">
                        <span className="h-1.5 w-1.5 rounded-full bg-gold" />
                    </span>
                    <span className="mt-2 text-[10px] font-mono uppercase tracking-[0.3em] text-mute">Scroll</span>
                    <ArrowDown size={13} className="mt-1 text-gold" />
                </motion.div>
            </motion.div>
        </section>
    );
}
